import { useState, useEffect } from 'react';
import { getFilterAggregations } from '../utils/elasticsearchApi';
import { continents } from '../data/continentCountryData';

/**
 * Custom hook to fetch available countries from Elasticsearch aggregations
 * @param {Object} filters - Current filters that may affect which countries are available
 * @returns {Object} - { countries, loading, error, refetch }
 */
export const useElasticsearchCountries = (filters = {}) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get aggregations from Elasticsearch
      // We pass empty filters to get all available countries, or current filters to get filtered countries
      const result = await getFilterAggregations(filters);
      
      if (result.success && result.data.countries) {
        // Extract country names from the aggregation buckets and sort them
        const countryList = result.data.countries.buckets
          .map(bucket => ({
            name: bucket.key,
            count: bucket.doc_count
          }))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        
        setCountries(countryList);
      } else {
        console.warn('No countries found in Elasticsearch aggregations, using fallback static data');
        // Fallback to static data when Elasticsearch aggregations fail
        const staticCountries = continents
          .flatMap(continent => continent.countries)
          .map(country => ({ name: country, count: 0 }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setCountries(staticCountries);
      }
    } catch (err) {
      console.error('Error fetching countries from Elasticsearch:', err);
      setError(err.message || 'Failed to fetch countries');
      
      // Fallback to static data on error
      const staticCountries = continents
        .flatMap(continent => continent.countries)
        .map(country => ({ name: country, count: 0 }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCountries(staticCountries);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const refetch = () => {
    fetchCountries();
  };

  return {
    countries,
    loading,
    error,
    refetch
  };
};
